import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { RefreshCw, Sparkles, Lightbulb, BookOpen, Clapperboard, Play, Trash2, AlertTriangle, Film } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { BACKEND_URL, reportError } from './adminApi';

const POLL_INTERVAL_MS = 8000;

/**
 * Admin card: one tile per Ptitconseil with its illustration (regenerate) and its AI video
 * (generate in 2 modes, follow progress, preview, delete). Polls while a video is being generated.
 */
export function AdminConseilsMediaCard({ adminKey, refreshToken = 0, onMediaChanged }) {
  const [conseils, setConseils] = useState([]);
  const [regenerating, setRegenerating] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [preview, setPreview] = useState(null);
  const pollRef = useRef(null);

  const loadConseils = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/ptitconseils?key=${adminKey}&limit=60`);
      setConseils(res.data.items || []);
    } catch (err) {
      reportError('liste des Ptitconseils (admin)', err, () => toast.error('Impossible de charger les Ptitconseils'));
    }
  }, [adminKey]);

  useEffect(() => { loadConseils(); }, [loadConseils, refreshToken]);

  // Poll while at least one video is being generated
  const hasRunning = conseils.some((c) => c.video_status === 'running');
  useEffect(() => {
    if (!hasRunning) return undefined;
    pollRef.current = setInterval(loadConseils, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [hasRunning, loadConseils]);

  const regenerate = async (c, newStyle) => {
    setRegenerating(c._id);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/ptitconseils/${c._id}/regenerate-image?key=${adminKey}${newStyle ? `&style=${newStyle}` : ''}`);
      setConseils((prev) => prev.map((x) => (x._id === c._id ? { ...x, image_url: res.data.url, image_style: newStyle || x.image_style } : x)));
      toast.success('Illustration régénérée');
      onMediaChanged?.();
    } catch (err) {
      reportError('régénération image', err, () => toast.error(err?.response?.data?.detail || 'Régénération impossible'));
    } finally {
      setRegenerating(null);
    }
  };

  const backfill = async () => {
    setBackfilling(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/images/backfill-conseils?key=${adminKey}&limit=6`);
      toast.success(res.data.scheduled ? `${res.data.scheduled} illustration(s) en cours de génération (1 à 2 min)` : 'Tous les conseils sont déjà illustrés');
      setTimeout(() => { loadConseils(); onMediaChanged?.(); }, 45000);
    } catch (err) {
      reportError('backfill images', err, () => toast.error('Impossible de lancer la génération'));
    } finally {
      setBackfilling(false);
    }
  };

  const generateVideo = async (c, mode) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/ptitconseils/${c._id}/generate-video?key=${adminKey}&mode=${mode}`);
      setConseils((prev) => prev.map((x) => (x._id === c._id ? { ...x, video_status: 'running', video_mode: mode, video_error: null } : x)));
      toast.success(`Vidéo « ${res.data.mode_label || mode} » en cours (1 à 4 min, ≈ ${res.data.price_usd} $)`);
    } catch (err) {
      reportError('génération vidéo', err, () => toast.error(err?.response?.data?.detail || 'Impossible de lancer la vidéo'));
    }
  };

  const deleteVideo = async (c) => {
    if (!window.confirm('Supprimer la vidéo de ce conseil ? L\'image restera affichée.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/videos/${c.video_id}?key=${adminKey}`);
      setConseils((prev) => prev.map((x) => (x._id === c._id ? { ...x, video_url: null, video_id: null, video_status: null, video_mode: null } : x)));
      toast.success('Vidéo supprimée');
      onMediaChanged?.();
    } catch (err) {
      reportError('suppression vidéo', err, () => toast.error('Suppression impossible'));
    }
  };

  const missing = conseils.filter((c) => !c.image_url).length;
  const withVideo = conseils.filter((c) => c.video_url).length;

  return (
    <Card className="p-5 sm:p-6" data-testid="conseils-media-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-['Space_Grotesk'] font-semibold">Illustrations & vidéos des Ptitconseils</h3>
          <p className="text-xs text-muted-foreground" data-testid="conseils-media-summary">
            {missing > 0 ? `${missing} conseil(s) sans image · ` : 'Tous les conseils sont illustrés · '}
            {withVideo} vidéo(s) générée(s)
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={loadConseils} aria-label="Actualiser" data-testid="conseils-media-refresh"><RefreshCw className="h-4 w-4" /></Button>
          <Button size="sm" onClick={backfill} disabled={backfilling || missing === 0} data-testid="images-backfill-btn"><Sparkles className="h-4 w-4 mr-1.5" /> Illustrer les {Math.min(missing, 6)} manquants</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="conseils-images-grid">
        {conseils.map((c) => {
          const TIcon = c.type === 'tuto' ? BookOpen : Lightbulb;
          const running = c.video_status === 'running';
          return (
            <div key={c._id} className="rounded-xl border border-border overflow-hidden bg-card flex flex-col" data-testid="conseil-image-card" data-video-status={c.video_status || 'none'}>
              <div className="relative h-32 bg-[hsl(var(--secondary))]/60">
                {c.image_url ? <img src={c.image_url} alt="" className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">Pas encore d'image</div>}
                {(regenerating === c._id || running) && (
                  <div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center text-white gap-1" data-testid="conseil-media-busy">
                    <RefreshCw className="h-6 w-6 animate-spin" />
                    {running && <span className="text-[11px]">Vidéo en cours…</span>}
                  </div>
                )}
                <Badge className="absolute top-2 left-2 bg-white/90 text-foreground hover:bg-white text-[10px]"><TIcon className="h-3 w-3 mr-1" />{c.type === 'tuto' ? 'Tuto' : 'Astuce'}</Badge>
                {c.video_url && !running && (
                  <button type="button" onClick={() => setPreview(c)} className="absolute inset-0 flex items-center justify-center group" aria-label="Prévisualiser la vidéo" data-testid="conseil-video-preview-btn">
                    <span className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow group-hover:scale-110 transition-transform"><Play className="h-4 w-4 text-[hsl(var(--primary))] ml-0.5" /></span>
                  </button>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  {c.image_style && <Badge className="bg-black/60 text-white hover:bg-black/60 text-[10px]">{c.image_style === 'photo' ? 'Photo' : 'Illustration'}</Badge>}
                  {c.video_url && <Badge className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))] text-[10px]" data-testid="conseil-video-badge"><Film className="h-3 w-3 mr-1" />Vidéo</Badge>}
                </div>
              </div>
              <div className="p-3 flex flex-col gap-2 flex-1">
                <p className="text-xs font-medium line-clamp-2">{c.titre}</p>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 text-[11px] flex-1" disabled={regenerating === c._id} onClick={() => regenerate(c, 'illustration')} data-testid="conseil-regen-illustration">Illustration</Button>
                  <Button size="sm" variant="outline" className="h-7 text-[11px] flex-1" disabled={regenerating === c._id} onClick={() => regenerate(c, 'photo')} data-testid="conseil-regen-photo">Photo</Button>
                </div>
                <div className="flex gap-1.5 mt-auto">
                  <Button size="sm" className="h-7 text-[11px] flex-1" disabled={running} onClick={() => generateVideo(c, 'animate')} data-testid="conseil-video-animate"><Clapperboard className="h-3 w-3 mr-1" /> Animer l'image</Button>
                  <Button size="sm" variant="secondary" className="h-7 text-[11px] flex-1" disabled={running} onClick={() => generateVideo(c, 'text')} data-testid="conseil-video-text">Texte → vidéo</Button>
                  {c.video_url && !running && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10" onClick={() => deleteVideo(c)} aria-label="Supprimer la vidéo" data-testid="conseil-video-delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                  )}
                </div>
                {c.video_status === 'error' && (
                  <p className="text-[11px] text-destructive flex items-start gap-1" data-testid="conseil-video-error"><AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" /><span className="line-clamp-2" title={c.video_error}>{c.video_error || 'La génération vidéo a échoué'}</span></p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!preview} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="max-w-3xl" data-testid="video-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk']">{preview?.titre}</DialogTitle>
            <DialogDescription>Vidéo générée par IA ({preview?.video_mode === 'text' ? 'texte → vidéo' : 'image animée'}). Elle est affichée à la place de l'image sur le site.</DialogDescription>
          </DialogHeader>
          {preview?.video_url && (
            <video src={preview.video_url} poster={preview.image_url || undefined} controls autoPlay muted loop playsInline className="w-full rounded-lg bg-black" data-testid="video-preview-player" />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
